import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService, Match, PlayerStats } from '../services/api';
import MatchPhotoCarousel from '../components/MatchPhotoCarousel';

console.log('MatchDetail component imported');

// Dummy icons for illustration
const PhotoIcon = () => (
  <span role="img" aria-label="photo" style={{ fontSize: 36 }}>🖼️</span>
);
const YouTubeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="red"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
);

const tableHeaderStyle = {
  background: '#26a69a',
  color: 'white',
  fontWeight: 600,
  textAlign: 'center' as const,
  fontSize: 14,
  padding: '8px 4px',
  border: '1px solid #ddd'
};
const tableCellStyle = {
  background: '#e3f2fd',
  textAlign: 'center' as const,
  fontSize: 13,
  padding: '6px 4px',
  border: '1px solid #ddd'
};

const MatchDetail: React.FC = () => {
  console.log('MatchDetail component rendered');
  const { matchId } = useParams<{ matchId: string }>();
  console.log('MatchDetail params:', { matchId });
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<number>(1);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  // Logo handling (same as Schedule component)
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());

  const getTeamLogoUrl = (teamId: string): string => {
    return `https://pub-e0ccb659989a49a297dc22911f822b08.r2.dev/photos/teams/logos/${teamId}.png`;
  };

  const handleLogoError = (teamId: string) => {
    setFailedLogos(prev => new Set(Array.from(prev).concat(teamId)));
  };

  const logoFailed = (teamId: string): boolean => {
    return failedLogos.has(teamId);
  };

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        if (!matchId) return;
        console.log('Fetching match with ID:', matchId);
        console.log('API URL:', `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/matches/${matchId}`);
        const data = await apiService.getMatchDetail(matchId);
        console.log('Match data received:', data);
        setMatch(data);
      } catch (err) {
        console.error('Error fetching match:', err);
        setError('Failed to load match data');
      } finally {
        setLoading(false);
      }
    };
    fetchMatch();
  }, [matchId]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>;
  if (error || !match) return <div style={{ textAlign: 'center', color: 'red', padding: 40 }}>{error || 'Match not found'}</div>;

  // Helper: format date/time
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const formatTime = (timeString?: string) => timeString ? timeString.substring(0, 5) : '';

  // Get quarter scores from the match data
  const team1Scores = [
    match.team1q1 || 0,
    match.team1q2 || 0,
    match.team1q3 || 0,
    match.team1q4 || 0,
    match.team1ot1 || 0
  ];
  const team2Scores = [
    match.team2q1 || 0,
    match.team2q2 || 0,
    match.team2q3 || 0,
    match.team2q4 || 0,
    match.team2ot1 || 0
  ];

  // YouTube links
  const youtubeLinks = [match.ytq1, match.ytq2, match.ytq3, match.ytq4, match.ytot];

  // Player stats
  const team1Stats: PlayerStats[] = match.team1_stats || [];
  const team2Stats: PlayerStats[] = match.team2_stats || [];

  // Calculate percentages
  const calculatePercentage = (made: number, attempted: number) => {
    if (attempted === 0) return 0;
    return Math.round((made / attempted) * 100);
  };

  // NBA Efficiency: (PTS + REB + AST + ST + BS) - (TO + FG misses + FT misses)
  const calculateEfficiency = (p: PlayerStats) => {
    const pts = p.pts || 0;
    const reb = p.reb || 0;
    const ast = p.ast || 0;
    const st = p.st || 0;
    const bs = p.bs || 0;
    const to = p.to || 0;
    const fgMisses = ((p.two_pa || 0) - (p.two_pm || 0)) + ((p.three_pa || 0) - (p.three_pm || 0));
    const ftMisses = (p.fta || 0) - (p.ftm || 0);
    return (pts + reb + ast + st + bs) - (to + fgMisses + ftMisses);
  };

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Sort players by jersey number: 0 first, then numbers ascending, then '-'
  const sortPlayersByJersey = (players: any[]) => {
    return [...players].sort((a, b) => {
      let jerseyA = a.player?.jersey;
      let jerseyB = b.player?.jersey;

      // Handle null/undefined jersey numbers
      if (jerseyA === null || jerseyA === undefined) jerseyA = '-';
      if (jerseyB === null || jerseyB === undefined) jerseyB = '-';

      // Convert to numbers for comparison
      const numA = jerseyA === '-' ? Infinity : Number(jerseyA);
      const numB = jerseyB === '-' ? Infinity : Number(jerseyB);

      // Special case: 0 should come first
      if (numA === 0 && numB !== 0) return -1;
      if (numB === 0 && numA !== 0) return 1;

      // Then sort by numeric value
      return numA - numB;
    });
  };

  // Get current video ID
  const currentVideoId = getYouTubeVideoId(youtubeLinks[selectedQuarter - 1] || '');

  // Check if any quarters have videos
  const hasVideos = youtubeLinks.some(link => link && link !== '');

  // Quarter names
  const quarterNames = ['Q1', 'Q2', 'Q3', 'Q4', 'OT'];

  return (
    <div className="modern-container">
      <div className="modern-page-card">
        {/* Header with Back Button */}
        <div style={{ marginBottom: 24 }}>
          <Link to={`/summer-league/${match.year}/schedule`} style={{
            textDecoration: 'none',
            color: '#1976d2',
            fontSize: 16,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600
          }}>
            ← 返回
          </Link>
        </div>

        {/* Match Info Bar */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginBottom: 24,
          fontSize: 14,
          color: '#64748b',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span>📅 {formatDate(match.date)}</span>
              <span>🕒 {formatTime(match.time)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>📍 {match.venue}</span>
            </div>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#0f172a' }}>
              {match.group} - Match #{match.match_no}
            </div>
          </div>
        </div>

        {/* Scoreboard */}
        <div className="scoreboard-wrapper">
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: isDesktop ? 48 : 20
          }}>
            <Link
              to={`/summer-league/${match.year}/${match.group}/teams/${match.team1.id}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{ textAlign: 'center', cursor: 'pointer' }}>
                {!logoFailed(match.team1.id) ? (
                  <img
                    src={getTeamLogoUrl(match.team1.id)}
                    alt={`${match.team1.name} Logo`}
                    style={{
                      maxWidth: isDesktop ? '75px' : '55px',
                      maxHeight: isDesktop ? '75px' : '55px',
                      width: 'auto',
                      height: 'auto',
                      borderRadius: '12px',
                      objectFit: 'contain',
                      marginBottom: 8,
                      backgroundColor: '#ffffff',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                      padding: 4,
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onError={() => {
                      handleLogoError(match.team1.id);
                    }}
                  />
                ) : (
                  <div style={{
                    width: isDesktop ? 75 : 55,
                    height: isDesktop ? 75 : 55,
                    borderRadius: '12px',
                    background: '#1976d2',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isDesktop ? 24 : 18,
                    fontWeight: 'bold',
                    marginBottom: 8,
                    transition: 'transform 0.2s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {match.team1.name.charAt(0)}
                  </div>
                )}
                <div style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#1e293b',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  maxWidth: '120px',
                  lineHeight: '1.2'
                }}>
                  {match.team1.name}
                </div>
              </div>
            </Link>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              margin: '16px 0',
              fontSize: isDesktop ? 36 : 28,
              fontWeight: 800,
              color: '#0f172a',
              flexWrap: 'nowrap',
              whiteSpace: 'nowrap'
            }}>
              <span>{match.score1 ?? '-'}</span>
              <span style={{ color: '#94a3b8' }}>:</span>
              <span>{match.score2 ?? '-'}</span>
            </div>

            <Link
              to={`/summer-league/${match.year}/${match.group}/teams/${match.team2.id}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{ textAlign: 'center', cursor: 'pointer' }}>
                {!logoFailed(match.team2.id) ? (
                  <img
                    src={getTeamLogoUrl(match.team2.id)}
                    alt={`${match.team2.name} Logo`}
                    style={{
                      maxWidth: isDesktop ? '75px' : '55px',
                      maxHeight: isDesktop ? '75px' : '55px',
                      width: 'auto',
                      height: 'auto',
                      borderRadius: '12px',
                      objectFit: 'contain',
                      marginBottom: 8,
                      backgroundColor: '#ffffff',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                      padding: 4,
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onError={() => {
                      handleLogoError(match.team2.id);
                    }}
                  />
                ) : (
                  <div style={{
                    width: isDesktop ? 75 : 55,
                    height: isDesktop ? 75 : 55,
                    borderRadius: '12px',
                    background: '#dc3545',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isDesktop ? 24 : 18,
                    fontWeight: 'bold',
                    marginBottom: 8,
                    transition: 'transform 0.2s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {match.team2.name.charAt(0)}
                  </div>
                )}
                <div style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#1e293b',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  maxWidth: '120px',
                  lineHeight: '1.2'
                }}>
                  {match.team2.name}
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Quarter Scores */}
        <div style={{
          margin: '0 auto 32px auto',
          width: isDesktop ? '65%' : '100%',
          boxSizing: 'border-box'
        }}>
          <h3 style={{ textAlign: 'center', marginBottom: 16, color: '#0f172a', fontSize: isDesktop ? 20 : 18, fontWeight: 700 }}>Quarter Scores</h3>

          <div className="modern-table-wrapper">
            <table className="modern-table" style={{ tableLayout: 'fixed', display: 'table', width: '100%' }}>
              <thead>
                <tr>
                  <th className="align-left" style={{ width: isDesktop ? '25%' : '32%' }}>球隊</th>
                  {['Q1', 'Q2', 'Q3', 'Q4', 'OT'].map((q) => (
                    <th key={q} style={{ width: isDesktop ? '12%' : '10%' }}>{q}</th>
                  ))}
                  <th className="total-stat" style={{ width: isDesktop ? '15%' : '18%' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Team 1 Row */}
                <tr>
                  <td className="align-left" style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {!logoFailed(match.team1.id) ? (
                        <img
                          src={getTeamLogoUrl(match.team1.id)}
                          alt=""
                          style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }}
                          onError={() => handleLogoError(match.team1.id)}
                        />
                      ) : (
                        <div style={{
                          width: 22,
                          height: 22,
                          borderRadius: 4,
                          background: '#1976d2',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 'bold'
                        }}>
                          {match.team1.name.charAt(0)}
                        </div>
                      )}
                      <span>{match.team1.name}</span>
                    </div>
                  </td>
                  {team1Scores.map((score, i) => (
                    <td key={i}>{score || 0}</td>
                  ))}
                  <td className="total-stat" style={{ color: '#1976d2' }}>{match.score1 || 0}</td>
                </tr>

                {/* Team 2 Row */}
                <tr>
                  <td className="align-left" style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {!logoFailed(match.team2.id) ? (
                        <img
                          src={getTeamLogoUrl(match.team2.id)}
                          alt=""
                          style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }}
                          onError={() => handleLogoError(match.team2.id)}
                        />
                      ) : (
                        <div style={{
                          width: 22,
                          height: 22,
                          borderRadius: 4,
                          background: '#dc3545',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 'bold'
                        }}>
                          {match.team2.name.charAt(0)}
                        </div>
                      )}
                      <span>{match.team2.name}</span>
                    </div>
                  </td>
                  {team2Scores.map((score, i) => (
                    <td key={i}>{score || 0}</td>
                  ))}
                  <td className="total-stat" style={{ color: '#dc3545' }}>{match.score2 || 0}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* YouTube Video Player with Quarter Tabs */}
        {hasVideos && (
          <div style={{ margin: '32px 0' }}>
            <h3 style={{ textAlign: 'center', marginBottom: 16, color: '#0f172a', fontWeight: 700 }}>比賽影片</h3>

            {/* Quarter Tabs */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 16,
              flexWrap: 'wrap',
              gap: 8
            }}>
              {quarterNames.map((quarter, index) => {
                const hasVideo = youtubeLinks[index] && youtubeLinks[index] !== '';
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedQuarter(index + 1)}
                    className={`video-tab-btn ${selectedQuarter === index + 1 ? 'active' : ''}`}
                    disabled={!hasVideo}
                  >
                    {quarter}
                  </button>
                );
              })}
            </div>

            {/* YouTube Player */}
            {currentVideoId ? (
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: isDesktop ? 960 : 600,
                margin: '0 auto',
                background: '#000',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
              }}>
                <iframe
                  width="100%"
                  height={isDesktop ? 540 : 280}
                  src={`https://www.youtube.com/embed/${currentVideoId}`}
                  title={`Quarter ${selectedQuarter} Video`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ display: 'block' }}
                />
              </div>
            ) : (
              <div style={{
                width: '100%',
                maxWidth: 600,
                height: 350,
                margin: '0 auto',
                background: '#f8fafc',
                border: '2px dashed #cbd5e1',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                fontSize: 16
              }}>
                No video available for Quarter {selectedQuarter}
              </div>
            )}
          </div>
        )}

        {/* Player Box Scores: Team 1 */}
        <div className="modern-section-card">
          <h2 className="modern-section-title">
            <Link to={`/summer-league/${match.year}/${match.group}/teams/${match.team1.id}`}>
              {match.team1.name}
            </Link>
          </h2>
          <div className="modern-table-wrapper">
            <table className="modern-table boxscore-table">
              <thead>
                <tr>
                  <th className="boxscore-jersey">#</th>
                  <th className="align-left boxscore-name">Name</th>
                  <th>2PM</th>
                  <th>2PA</th>
                  <th>2P%</th>
                  <th>3PM</th>
                  <th>3PA</th>
                  <th>3P%</th>
                  <th>FTM</th>
                  <th>FTA</th>
                  <th>FT%</th>
                  <th>OREB</th>
                  <th>DREB</th>
                  <th>REB</th>
                  <th>AST</th>
                  <th>TO</th>
                  <th>ST</th>
                  <th>BS</th>
                  <th>PF</th>
                  <th className="highlight-stat">PTS</th>
                  <th>EFF</th>
                </tr>
              </thead>
              <tbody>
                {sortPlayersByJersey(team1Stats).map((p, i) => (
                  <tr key={i}>
                    <td className="boxscore-jersey">{p.player?.jersey !== null && p.player?.jersey !== undefined ? p.player.jersey : '-'}</td>
                    <td className="align-left boxscore-name">
                      {p.player?.id ? (
                        <Link to={`/players/${p.player.id}`} className="modern-link">
                          {p.player.name}
                        </Link>
                      ) : (
                        p.player?.name || '-'
                      )}
                    </td>
                    <td>{p.two_pm || 0}</td>
                    <td>{p.two_pa || 0}</td>
                    <td>{calculatePercentage(p.two_pm || 0, p.two_pa || 0)}%</td>
                    <td>{p.three_pm || 0}</td>
                    <td>{p.three_pa || 0}</td>
                    <td>{calculatePercentage(p.three_pm || 0, p.three_pa || 0)}%</td>
                    <td>{p.ftm || 0}</td>
                    <td>{p.fta || 0}</td>
                    <td>{calculatePercentage(p.ftm || 0, p.fta || 0)}%</td>
                    <td>{p.oreb || 0}</td>
                    <td>{p.dreb || 0}</td>
                    <td>{p.reb || 0}</td>
                    <td>{p.ast || 0}</td>
                    <td>{p.to || 0}</td>
                    <td>{p.st || 0}</td>
                    <td>{p.bs || 0}</td>
                    <td>{p.pf || 0}</td>
                    <td className="highlight-stat">{p.pts || 0}</td>
                    <td>{calculateEfficiency(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Player Box Scores: Team 2 */}
        <div className="modern-section-card">
          <h2 className="modern-section-title">
            <Link to={`/summer-league/${match.year}/${match.group}/teams/${match.team2.id}`}>
              {match.team2.name}
            </Link>
          </h2>
          <div className="modern-table-wrapper">
            <table className="modern-table boxscore-table">
              <thead>
                <tr>
                  <th className="boxscore-jersey">#</th>
                  <th className="align-left boxscore-name">Name</th>
                  <th>2PM</th>
                  <th>2PA</th>
                  <th>2P%</th>
                  <th>3PM</th>
                  <th>3PA</th>
                  <th>3P%</th>
                  <th>FTM</th>
                  <th>FTA</th>
                  <th>FT%</th>
                  <th>OREB</th>
                  <th>DREB</th>
                  <th>REB</th>
                  <th>AST</th>
                  <th>TO</th>
                  <th>ST</th>
                  <th>BS</th>
                  <th>PF</th>
                  <th className="highlight-stat">PTS</th>
                  <th>EFF</th>
                </tr>
              </thead>
              <tbody>
                {sortPlayersByJersey(team2Stats).map((p, i) => (
                  <tr key={i}>
                    <td className="boxscore-jersey">{p.player?.jersey !== null && p.player?.jersey !== undefined ? p.player.jersey : '-'}</td>
                    <td className="align-left boxscore-name">
                      {p.player?.id ? (
                        <Link to={`/players/${p.player.id}`} className="modern-link">
                          {p.player.name}
                        </Link>
                      ) : (
                        p.player?.name || '-'
                      )}
                    </td>
                    <td>{p.two_pm || 0}</td>
                    <td>{p.two_pa || 0}</td>
                    <td>{calculatePercentage(p.two_pm || 0, p.two_pa || 0)}%</td>
                    <td>{p.three_pm || 0}</td>
                    <td>{p.three_pa || 0}</td>
                    <td>{calculatePercentage(p.three_pm || 0, p.three_pa || 0)}%</td>
                    <td>{p.ftm || 0}</td>
                    <td>{p.fta || 0}</td>
                    <td>{calculatePercentage(p.ftm || 0, p.fta || 0)}%</td>
                    <td>{p.oreb || 0}</td>
                    <td>{p.dreb || 0}</td>
                    <td>{p.reb || 0}</td>
                    <td>{p.ast || 0}</td>
                    <td>{p.to || 0}</td>
                    <td>{p.st || 0}</td>
                    <td>{p.bs || 0}</td>
                    <td>{p.pf || 0}</td>
                    <td className="highlight-stat">{p.pts || 0}</td>
                    <td>{calculateEfficiency(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Match Photo Carousel */}
        <div style={{ margin: '32px auto' }}>
          <h3 style={{ textAlign: 'center', marginBottom: 16, color: '#0f172a', fontSize: isDesktop ? 20 : 18, fontWeight: 700 }}>Highlights</h3>
          <MatchPhotoCarousel matchId={match.id} />
        </div>

      </div>
    </div>
  );
};

export default MatchDetail; 